import { useHits } from 'react-instantsearch';
import { useMemo, useState } from 'react';
import SkuCard from '@/components/common/cards/sku/SkuCard';
import { SkuDTO } from '@/types/api/sku';
import Image from '@/components/common/SpiritoryImage';
import SkuDescription from '@/components/skuDescription';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { StKilianEnum } from '@/types/translations/enums';
import { toDe, groupAsksBySku, filterHitsBySellerAsks } from '@/utilites/sku-operations';

interface SellerMobileBidsDisplayProps {
  viewMode: 'grid' | 'list';
  asks: any[];
  onCardClick: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    sku: SkuDTO,
    groupedAsks?: any
  ) => void;
  maxBottles?: number;
}

export default function SellerMobileBidsDisplay({
  viewMode,
  asks,
  onCardClick,
  maxBottles,
}: SellerMobileBidsDisplayProps) {
  const { hits } = useHits();
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { translate: translateStKilian } = useTypedTranslation<StKilianEnum>('st_kilian');
  const [showAll, setShowAll] = useState(false);

  // Group asks by SKU slug for display
  const groupedAsks = useMemo(() => {
    return groupAsksBySku(asks);
  }, [asks]);

  // Get all filtered hits (without limit) to check if we need to show the "Show All" button
  const allFilteredHits = useMemo(() => {
    return filterHitsBySellerAsks(hits, asks);
  }, [hits, asks]);

  // Filter hits to only show SKUs that have asks from this seller
  const filteredHits = useMemo(() => {
    return filterHitsBySellerAsks(hits, asks, maxBottles, showAll);
  }, [hits, asks, maxBottles, showAll]);

  // Check if we should show the "Show All" button
  const shouldShowAllButton = maxBottles && allFilteredHits.length > maxBottles;

  if (filteredHits.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
        <p className="tw-text-center tw-text-gray-500">No offers available from this seller.</p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div>
        <ul className="tw-grid tw-grid-cols-2 tw-gap-3 tw-p-0">
          {filteredHits.map((hit, index) => {
            const sku = hit as unknown as SkuDTO;
            const skuAsks = groupedAsks[sku.slug] || [];
            const lowestAsk = skuAsks.length > 0 ? Math.min(...skuAsks.map(ask => ask.value)) : 0;

            return (
              <li key={sku.slug || index} className="tw-list-none">
                <SkuCard
                  sku={sku}
                  askValue={lowestAsk}
                  isSellerCard={true}
                  onCardClick={e => onCardClick(e, sku, skuAsks)}
                />
              </li>
            );
          })}
        </ul>

        {/* Show All Button */}
        {shouldShowAllButton && !showAll && (
          <div className="tw-mt-4 tw-flex tw-justify-center">
            <button
              onClick={() => setShowAll(true)}
              className="tw-cursor-pointer tw-rounded-lg tw-border-none tw-bg-gray-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-200"
            >
              {translateStKilian(StKilianEnum.show_all_private_casks) || 'Show All'} (
              {allFilteredHits.length})
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <ul className="gap-4 tw-flex tw-flex-col tw-p-0">
        <li className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 !tw-border-solid tw-border-cool-gray tw-pb-3 tw-font-plusJakartaSans tw-text-xs tw-font-semibold tw-text-cool-gray">
          <span>{translate(CommonEnum.name).toUpperCase()}</span>
          <span>{translate(CommonEnum.action).toUpperCase()}</span>
        </li>

        {filteredHits.map((hit, index) => {
          const sku = hit as unknown as SkuDTO;
          const skuAsks = groupedAsks[sku.slug] || [];
          const lowestAsk = skuAsks.length > 0 ? Math.min(...skuAsks.map(ask => ask.value)) : 0;

          return (
            <li key={sku.slug || index} className="tw-list-none">
              <button
                className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-2 tw-transition-colors hover:tw-bg-gray-50 active:tw-bg-gray-100"
                onClick={e => onCardClick(e, sku, skuAsks)}
                aria-label={`View details for ${sku.name}`}
              >
                <div className="tw-flex tw-items-center tw-gap-3">
                  <div>
                    <Image
                      src={sku.mainImageUrl || ''}
                      alt={sku.name || ''}
                      width={74}
                      height={74}
                      className="tw-rounded-lg tw-border !tw-border-solid tw-border-platinum-gray tw-object-contain tw-p-1"
                    />
                  </div>
                  <div>
                    <p className="tw-flex tw-flex-col tw-gap-1 tw-p-0 tw-text-start tw-font-plusJakartaSans tw-text-xs tw-font-medium tw-text-slate-gray">
                      <strong className="tw-line-clamp-1 tw-font-plusJakartaSans tw-text-sm tw-font-semibold tw-text-textBlack">
                        {sku.name}
                      </strong>
                      <SkuDescription
                        skuBottleSizeInMl={sku.bottleSizeInMl || null}
                        skuVolumeInPercent={sku.volumeInPercent || null}
                        skuReleaseDate={sku.releaseDate || null}
                      />
                    </p>
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-bg-[#15253F1A] tw-p-2 tw-px-[20px] tw-py-[10px] tw-text-center tw-font-plusJakartaSans tw-text-xs-custom tw-font-semibold tw-text-text">
                  <span>{translate(CommonEnum['ask'])}</span>
                  <span>
                    {lowestAsk && lowestAsk > 0
                      ? toDe(lowestAsk, {
                          withDigits: false,
                          withSymbol: true,
                        })
                      : '--'}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Show All Button */}
      {shouldShowAllButton && !showAll && (
        <div className="tw-mt-4 tw-flex tw-justify-center">
          <button
            onClick={() => setShowAll(true)}
            className="tw-cursor-pointer tw-rounded-lg tw-border-none tw-bg-gray-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-200"
          >
            {translate(CommonEnum.all) || 'Show All'} ({allFilteredHits.length})
          </button>
        </div>
      )}
    </div>
  );
}
